import { useState } from "react";

import { Coordinate, IAddress } from "common/types";
import { toNumberDate } from "common/utils/dates";
import errorImages from "common/constants/errorImages";
import { Optional } from "common/utils/types";

import Modal from "client/common/components/deprecated-modals/Modal";
import PillButton from "client/common/components/buttons/PillButton";
import MapBox from "client/common/components/content/MapBox";
import ModalLayout from "client/common/components/deprecated-modals/ModalLayout";
import Tabs from "client/common/components/buttons/Tabs";
import Timeline from "client/common/components/content/Timeline";

import { IGroupedOrderItemTracker } from "../types";

import styles from "../styles/OrderTrackingModal.module.less";

type ShipmentTrackingProps = {
  tracker: IGroupedOrderItemTracker;
  address: Optional<IAddress>;
};

const ShipmentTracking = ({ tracker, address }: ShipmentTrackingProps) => {
  const coordinates =
    (tracker?.tracker_details
      .map((detail) => detail.coordinates)
      .filter((coord) => !!coord) as Coordinate[]) || [];

  return (
    <>
      <div className={styles.info}>
        <div>
          <p className={styles.status}>{tracker.status_display}</p>
          {tracker.last_updated && (
            <p className={styles.updated}>Last Updated: {tracker.last_updated}</p>
          )}
        </div>
        {!!tracker.tracking_url && (
          <PillButton variant="secondary" href={tracker.tracking_url}>
            Track with {tracker.carrier}
          </PillButton>
        )}
      </div>
      {tracker && (
        <>
          {address && <MapBox address={address} markers={coordinates} paths={coordinates} />}
          <Timeline
            events={tracker.tracker_details.map((detail) => ({
              key: `${detail.id}`,
              header: detail.message,
              content: detail.datetime ? `${toNumberDate(detail.datetime)} - ${detail.since}` : "",
              status: "default",
            }))}
          />
        </>
      )}
    </>
  );
};

type OrderTrackingModalProps = {
  visible: boolean;
  onClose: () => void;
  trackers: Optional<IGroupedOrderItemTracker[]>;
  shippingAddress: Optional<IAddress>;
};

const OrderTrackingModal = ({
  trackers,
  visible,
  onClose,
  shippingAddress,
}: OrderTrackingModalProps) => {
  const tabs = trackers || [];
  const [activeTab, setActiveTab] = useState(tabs.length ? tabs[0] : undefined);

  const renderActiveTab = () => {
    if (!activeTab) {
      return (
        <ModalLayout.Error
          title="Tracking not ready"
          description="Tracking is not ready yet for this order! We'll let you know as soon as this shipment is on the way."
          image={errorImages.EmptyOrders}
          imageAlt="Nothing found"
        />
      );
    }

    return <ShipmentTracking tracker={activeTab} address={shippingAddress} />;
  };

  const renderContent = () => {
    if (!tabs.length) {
      return (
        <ModalLayout.Error
          title="Tracking not ready"
          description="Tracking is not ready yet for this order! We'll let you know as soon as this shipment is on the way."
          image={errorImages.EmptyOrders}
          imageAlt="Nothing found"
        />
      );
    }

    return (
      <>
        {tabs.length > 1 && (
          <Tabs>
            {tabs.map((s, idx) => (
              <Tabs.Button
                key={s.id}
                onClick={() => setActiveTab(s)}
                active={s.id === activeTab?.id}
                label={`Shipment ${idx + 1}`}
              />
            ))}
          </Tabs>
        )}
        <ModalLayout.Content>{renderActiveTab()}</ModalLayout.Content>
      </>
    );
  };

  return (
    <Modal visible={visible} onClose={onClose}>
      <ModalLayout>
        <ModalLayout.Header title="Track your order" onClose={onClose} />
        {renderContent()}
      </ModalLayout>
    </Modal>
  );
};

export default OrderTrackingModal;
